import { useEffect, useState } from 'react';
import { I } from '../components/icons.jsx';
import { useMetrics, useHealth, deriveRates } from '../data/use-metrics.js';
import { homeHeading } from './home-heading.js';

const HISTORY_KEY = 'kinedb.qc.history'; // shared with QueryConsole

// User's own query history from QueryConsole — also a localStorage event.
// Re-reads on focus so a query run in another tab updates the Home card.
function useQueryHistory() {
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    const reload = () => {
      try { setHistory(JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]')); }
      catch { /* ignore */ }
    };
    window.addEventListener('storage', reload);
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('focus', reload);
    };
  }, []);
  return history;
}

// ── Helpers ─────────────────────────────────────────────────────────

function fmtCount(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const v = Math.round(n);
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'B';
  if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)         return (v / 1_000).toFixed(1) + 'k';
  return v.toLocaleString();
}

function fmtBytes(b) {
  if (b == null || !Number.isFinite(b)) return '—';
  if (b >= 1024 ** 4) return (b / 1024 ** 4).toFixed(1) + ' TB';
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(1) + ' MB';
  if (b >= 1024)      return (b / 1024).toFixed(1) + ' KB';
  return Math.round(b) + ' B';
}

function relTime(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5)     return 'just now';
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function PendingValue({ children, color = 'var(--app-fg4)', tip }) {
  return <span style={{ color, fontFamily: 'var(--app-mono)', fontWeight: 600 }} title={tip}>{children}</span>;
}

// ── Page ────────────────────────────────────────────────────────────

// B1863: `username` is the session user. It is null on a server without login.
export function HomePage({ onNavigate, username }) {
  const heading = homeHeading({ username, now: new Date() });
  const { latest: metrics, prev, sampledAt, prevSampledAt, error: metricsErr } = useMetrics('', 1500);
  const { health, error: healthErr } = useHealth('', 5000);
  const history = useQueryHistory();
  const rates = deriveRates(metrics, prev, sampledAt, prevSampledAt) ?? { readsPerSec: 0, writesPerSec: 0 };

  const status = (() => {
    if (healthErr) return { label: 'Unreachable.', color: 'var(--orange)' };
    if (!health)   return { label: '…',            color: 'var(--app-fg3)' };
    return /^ok\b/i.test(health.status)
      ? { label: 'Healthy.',  color: 'var(--green)'  }
      : { label: 'Degraded.', color: 'var(--orange)' };
  })();

  const nodeCount = health?.peers?.length ?? null;

  const queriesTotal     = metrics?.['dml.queries_total'];
  const bytesWritten     = metrics?.['io.bytes_written_total'];
  const slowTotal        = metrics?.['query.slow_total'];
  const slowThresholdMs  = metrics?.['query.slow_threshold_us'] != null
    ? Math.round(metrics['query.slow_threshold_us'] / 1000)
    : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">{heading.crumb}</div>
          <h1>{heading.title}</h1>
        </div>
        <div className="actions">
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate('query')}><I.Play size={11} /> New query</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--app-fg3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>Your cluster, right now</div>
            <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 8 }}>
              <span style={{ color: status.color }}>{status.label}</span>{' '}
              <span style={{ color: 'var(--app-fg2)' }}>Serving</span>{' '}
              <span style={{ background: 'linear-gradient(135deg, #00FFD1, #0099FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                {fmtCount(rates.readsPerSec)} reads/s
              </span>
              <br />
              <span style={{ color: 'var(--app-fg2)' }}>across</span>{' '}
              {nodeCount != null ? `${nodeCount} node${nodeCount === 1 ? '' : 's'}` : '… nodes'}
              <span style={{ color: 'var(--app-fg2)' }}>, writing</span>{' '}
              <span style={{ color: 'var(--blue)' }}>{fmtCount(rates.writesPerSec)}/s</span>
              <span style={{ color: 'var(--app-fg2)' }}>.</span>
            </div>
            <div style={{ color: 'var(--app-fg3)', fontSize: 14, maxWidth: 560 }}>
              {metricsErr
                ? <span style={{ color: 'var(--orange)' }}>Metrics unreachable: {metricsErr}</span>
                : <>Live numbers update every ~1.5s from <span className="mono" style={{ color: 'var(--cyan)' }}>SHOW METRICS</span>. Cluster status from <span className="mono" style={{ color: 'var(--cyan)' }}>/health</span>.</>}
            </div>
          </div>
          <div className="k-card">
            <div className="k-card-head"><span className="title">Cumulative</span></div>
            <div className="k-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Row label="Queries run">
                {queriesTotal != null
                  ? <PendingValue color="var(--app-fg1)">{fmtCount(queriesTotal)}</PendingValue>
                  : <PendingValue tip="Needs server counter dml.queries_total">—</PendingValue>}
              </Row>
              <Row label="Data written">
                {bytesWritten != null
                  ? <PendingValue color="var(--app-fg1)">{fmtBytes(bytesWritten)}</PendingValue>
                  : <PendingValue tip="Needs server counter io.bytes_written_total">—</PendingValue>}
              </Row>
              <Row label={slowThresholdMs != null ? `Slow queries (>${slowThresholdMs}ms)` : 'Slow queries'}>
                {slowTotal != null
                  ? <PendingValue color={slowTotal > 0 ? 'var(--orange)' : 'var(--app-fg1)'}>{fmtCount(slowTotal)}</PendingValue>
                  : <PendingValue tip="Needs server counter query.slow_total">—</PendingValue>}
              </Row>
              <Row label="Memory in use">
                {health?.memory_total_bytes != null
                  ? <PendingValue color="var(--app-fg1)">{fmtBytes(health.memory_total_bytes)}</PendingValue>
                  : <PendingValue>—</PendingValue>}
              </Row>
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
          {[
            { ic: I.Query,   title: 'Run a query',       body: 'Open a fresh tab in the console.',                 to: 'query',   accent: 'var(--cyan)' },
            { ic: I.Reports, title: 'Open a report',     body: 'Daily revenue, slow queries, schema changes.',     to: 'reports', accent: 'var(--blue)' },
            { ic: I.Cluster, title: 'Check the cluster', body: 'See how nodes and RAFT groups are doing.',         to: 'cluster', accent: 'var(--green)' },
          ].map(c => (
            <button key={c.title} className="k-card" onClick={() => onNavigate(c.to)} style={{ textAlign: 'left', padding: 0, cursor: 'pointer' }}>
              <div className="k-card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, color: c.accent }}><c.ic size={18} /></div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--app-fg1)', marginBottom: 4 }}>{c.title}</div>
                <div style={{ fontSize: 13, color: 'var(--app-fg3)' }}>{c.body}</div>
              </div>
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="k-card">
            <div className="k-card-head">
              <span className="title">Your recent queries</span>
              <div className="actions">
                <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('query')}>Open console →</button>
              </div>
            </div>
            {history.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--app-fg3)', fontSize: 13, textAlign: 'center' }}>
                No queries yet. Run one in the console — it'll appear here.
              </div>
            ) : (
              <table className="k-table">
                <tbody>
                  {history.slice(0, 5).map((h, i) => (
                    <tr key={i}>
                      <td style={{ width: 1 }}>
                        <span className={'kdot' + (h.status === 'err' ? ' orange' : '')} style={{ marginRight: 8 }} />
                      </td>
                      <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12, maxWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.sql}</td>
                      <td className="num" style={{ whiteSpace: 'nowrap' }}>{h.ms}ms</td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--app-fg3)', textAlign: 'right' }}>{relTime(h.ts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="k-card">
            <div className="k-card-head"><span className="title">What's happening</span></div>
            <div style={{ padding: 24, color: 'var(--app-fg3)', fontSize: 13, lineHeight: 1.6 }}>
              The activity feed needs a server-side event stream (schema changes, node up/down, errors). Not wired yet — coming with cluster events.
              {nodeCount === 1 && (
                <div style={{ marginTop: 12, color: 'var(--app-fg2)' }}>
                  <I.Server size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Single-node cluster — peer events won't have much to show until you join more nodes.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'var(--app-fg2)' }}>{label}</span>
      {children}
    </div>
  );
}
